import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import VendorCard from '../../components/ui/VendorCard';
import SearchHeader from '../../components/ui/SearchHeader';
import CategoryFilter from '../../components/ui/CategoryFilter';
import LocationInput from '../../components/ui/LocationInput';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import vendorDataService from '../../services/vendorDataService';
import { VENDOR_CATEGORIES, CATEGORY_INFO } from '../../constants/categoryMapping';
import { useDebounce } from '../../hooks/useDebounce';

const AllVendors = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Debounce search query to reduce API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Build categories from database enum
  const categories = [
    { value: 'all', label: 'All Categories' },
    ...Object.entries(CATEGORY_INFO).map(([key, info]) => ({
      value: key,
      label: info.title
    }))
  ];

  const sortOptions = [
    { value: 'featured', label: 'Featured First' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'reviews', label: 'Most Reviews' }
  ];

  // Check if mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch vendors from database
  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      try {
        const result = await vendorDataService.getVendors({
          limit: 100,
          sortBy: 'is_featured'
        });

        if (result.success) {
          const transformedVendors = result.data.map(vendor => ({
            id: vendor.id,
            name: vendor.business_name,
            category: vendor.category,
            categoryName: CATEGORY_INFO[vendor.category]?.title || vendor.category,
            location: `${vendor.city}, ${vendor.state}`,
            rating: vendor.average_rating || 0,
            reviewCount: vendor.review_count || 0,
            startingPrice: vendor.starting_price,
            image: vendor.vendor_gallery?.[0]?.image_url || '/assets/images/no_image.png',
            verified: vendor.is_verified,
            featured: vendor.is_featured,
            description: vendor.description || '',
            specialties: vendor.services || []
          }));

          setVendors(transformedVendors);
          setFilteredVendors(transformedVendors);
        } else {
          console.error('Failed to fetch vendors:', result.error);
        }
      } catch (error) {
        console.error('Error fetching vendors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, []);

  // Fetch vendors with filters applied (using debounced search)
  useEffect(() => {
    const fetchFilteredVendors = async () => {
      setLoading(true);
      try {
        const filters = {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          search: debouncedSearchQuery || undefined,
          sortBy: sortBy === 'featured' ? 'is_featured' : 
                  sortBy === 'rating' ? 'average_rating' :
                  sortBy === 'price-low' ? 'starting_price' : 
                  sortBy === 'price-high' ? 'starting_price' : 'is_featured',
          sortOrder: sortBy === 'price-high' ? 'desc' : 'asc',
          limit: 100
        };

        const result = await vendorDataService.getVendors(filters);

        if (result.success) {
          const transformedVendors = result.data.map(vendor => ({
            id: vendor.id,
            name: vendor.business_name,
            description: vendor.description || '',
            category: vendor.category,
            categoryName: vendor.category_name || vendor.category,
            culturalSpecialization: vendor.cultural_specialties?.[0] || '',
            image: vendor.vendor_gallery?.[0]?.image_url || 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92',
            rating: vendor.average_rating || 0,
            reviewCount: vendor.review_count || 0,
            location: `${vendor.city}, ${vendor.state}`,
            city: vendor.city,
            state: vendor.state,
            startingPrice: vendor.starting_price,
            isVerified: vendor.is_verified,
            isFeatured: vendor.is_featured,
            totalBookings: vendor.total_bookings || 0
          }));

          let filtered = transformedVendors;

          // Apply location filter (client-side for now)
          if (selectedLocation) {
            filtered = filtered.filter(vendor =>
              vendor.location.toLowerCase().includes(selectedLocation.toLowerCase())
            );
          }

          setVendors(transformedVendors);
          setFilteredVendors(filtered);
        }
      } catch (error) {
        console.error('Error fetching vendors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredVendors();
  }, [debouncedSearchQuery, selectedCategory, selectedLocation, sortBy]);

  // Keep original filtering for immediate UI updates
  useEffect(() => {
    let filtered = [...vendors];

    // Apply location filter
    if (selectedLocation) {
      filtered = filtered.filter(vendor =>
        vendor.location.toLowerCase().includes(selectedLocation.toLowerCase())
      );
    }

    // Apply sorting (redundant but kept for consistency)
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'featured':
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return b.rating - a.rating;
        case 'rating':
          return b.rating - a.rating;
        case 'price-low':
          return a.startingPrice - b.startingPrice;
        case 'price-high':
          return b.startingPrice - a.startingPrice;
        case 'reviews':
          return b.reviewCount - a.reviewCount;
        default:
          return 0;
      }
    });

    setFilteredVendors(filtered);
  }, [vendors, searchQuery, selectedCategory, selectedLocation, sortBy]);

  const handleVendorClick = (vendorId) => {
    navigate(`/vendor-profile?id=${vendorId}`);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedLocation('');
    setSortBy('featured');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedLocation || sortBy !== 'featured';

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Group vendors by category for showcase
  const vendorsByCategory = vendors.reduce((acc, vendor) => {
    if (!acc[vendor.category]) {
      acc[vendor.category] = [];
    }
    acc[vendor.category].push(vendor);
    return acc;
  }, {});

  const VendorCarousel = ({ title, vendors, categoryKey }) => {
    if (!vendors || vendors.length === 0) return null;

    return (
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            {title}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => setSelectedCategory(categoryKey)}
            className="text-primary hover:text-primary-600"
          >
            View All
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.slice(0, 3).map((vendor) => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              onClick={() => handleVendorClick(vendor.id)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section - aligned with homepage style */}
      <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50 pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-text-primary">
              Discover All <span className="text-primary">Wedding Vendors</span>
            </h1>
            <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto font-body leading-relaxed">
              Find trusted South Asian wedding professionals for your special day.
              Browse by category or use our filters to find exactly what you need.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={20} color="#10b981" />
                <span>Verified Professionals</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Star" size={20} color="#f59e0b" />
                <span>Highly Rated</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Heart" size={20} color="#ef4444" />
                <span>Cultural Experts</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Toggle Button */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="outline"
                onClick={toggleFilter}
                iconName={isFilterOpen ? "X" : "Filter"}
                className="flex-1 justify-center"
              >
                {isFilterOpen ? 'Close Filters' : 'Show Filters'}
                {hasActiveFilters && !isFilterOpen && (
                  <span className="ml-2 bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                    {[searchQuery, selectedCategory !== 'all', selectedLocation, sortBy !== 'featured'].filter(Boolean).length}
                  </span>
                )}
              </Button>
            </div>

            {/* Mobile Filter Accordion */}
            {isFilterOpen && (
              <div className="bg-white rounded-xl shadow-soft border border-border p-6 mb-8">
                <div className="space-y-6">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Search Vendors
                    </label>
                    <SearchHeader
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      placeholder="Search by name, service..."
                      size="sm"
                    />
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Category
                    </label>
                    <CategoryFilter
                      categories={categories}
                      selectedCategory={selectedCategory}
                      onCategoryChange={setSelectedCategory}
                    />
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Location
                    </label>
                    <LocationInput
                      value={selectedLocation}
                      onChange={setSelectedLocation}
                      placeholder="City, State..."
                    />
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      onClick={handleClearFilters}
                      iconName="X"
                      className="w-full text-primary hover:text-primary-600"
                    >
                      Clear All Filters
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Desktop Sidebar Filter */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-soft border border-border p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    Filters
                  </h2>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      iconName="X"
                      className="text-primary hover:text-primary-600"
                    >
                      Clear
                    </Button>
                  )}
                </div>

                <div className="space-y-6">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      Search Vendors
                    </label>
                    <SearchHeader
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      placeholder="Search by name, service..."
                      size="sm"
                    />
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      Category
                    </label>
                    <CategoryFilter
                      categories={categories}
                      selectedCategory={selectedCategory}
                      onCategoryChange={setSelectedCategory}
                    />
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      Location
                    </label>
                    <LocationInput
                      value={selectedLocation}
                      onChange={setSelectedLocation}
                      placeholder="City, State..."
                    />
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-3">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Active Filters Summary */}
                {hasActiveFilters && (
                  <div className="mt-6 pt-6 border-t border-border">
                    <p className="text-sm text-text-secondary mb-2">Active Filters:</p>
                    <div className="flex flex-wrap gap-2">
                      {searchQuery && (
                        <span className="inline-flex items-center px-2 py-1 bg-primary-50 text-primary text-xs rounded-full">
                          Search: {searchQuery}
                        </span>
                      )}
                      {selectedCategory !== 'all' && (
                        <span className="inline-flex items-center px-2 py-1 bg-primary-50 text-primary text-xs rounded-full">
                          {categories.find(c => c.value === selectedCategory)?.label}
                        </span>
                      )}
                      {selectedLocation && (
                        <span className="inline-flex items-center px-2 py-1 bg-primary-50 text-primary text-xs rounded-full">
                          {selectedLocation}
                        </span>
                      )}
                      {sortBy !== 'featured' && (
                        <span className="inline-flex items-center px-2 py-1 bg-primary-50 text-primary text-xs rounded-full">
                          {sortOptions.find(s => s.value === sortBy)?.label}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading font-semibold text-2xl text-text-primary">
                  {loading ? 'Loading...' :
                    hasActiveFilters ? `${filteredVendors.length} Vendors Found` : 'Browse by Category'}
                </h2>
                {!loading && searchQuery && (
                  <p className="text-text-secondary mt-1 font-caption">
                    Results for "{searchQuery}"
                  </p>
                )}
                {!loading && !hasActiveFilters && (
                  <p className="text-text-secondary mt-1 font-caption">
                    Explore vendors by category or use filters to narrow your search
                  </p>
                )}
              </div>

              {!loading && filteredVendors.length > 0 && hasActiveFilters && (
                <div className="text-sm text-text-secondary font-caption">
                  Showing {filteredVendors.length} of {vendors.length} vendors
                </div>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-soft p-6 animate-pulse">
                    <div className="h-48 bg-surface-200 rounded-lg mb-4"></div>
                    <div className="h-4 bg-surface-200 rounded mb-2"></div>
                    <div className="h-4 bg-surface-200 rounded w-2/3 mb-4"></div>
                    <div className="h-3 bg-surface-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            )}

            {/* Category Carousels - Show when no active filters */}
            {!loading && !hasActiveFilters && (
              <div className="space-y-12">
                {Object.entries(vendorsByCategory).map(([categoryKey, categoryVendors]) => {
                  const categoryInfo = categories.find(c => c.value === categoryKey);
                  if (!categoryInfo || categoryInfo.value === 'all') return null;

                  return (
                    <VendorCarousel
                      key={categoryKey}
                      title={categoryInfo.label}
                      vendors={categoryVendors}
                      categoryKey={categoryKey}
                    />
                  );
                })}
              </div>
            )}

            {/* Filtered Results */}
            {!loading && hasActiveFilters && (
              <>
                {/* No Results State */}
                {filteredVendors.length === 0 && (
                  <div className="text-center py-16">
                    <Icon name="Search" size={64} color="#9ca3af" className="mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                      No vendors found
                    </h3>
                    <p className="text-text-secondary mb-6 font-caption">
                      Try adjusting your search criteria or browse all categories
                    </p>
                    <Button
                      variant="primary"
                      onClick={handleClearFilters}
                      iconName="RotateCcw"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}

                {/* Vendors Grid */}
                {filteredVendors.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredVendors.map((vendor) => (
                      <VendorCard
                        key={vendor.id}
                        vendor={vendor}
                        onClick={() => handleVendorClick(vendor.id)}
                      />
                    ))}
                  </div>
                )}

                {/* Load More Button */}
                {filteredVendors.length > 0 && filteredVendors.length >= 12 && (
                  <div className="text-center mt-12">
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="ChevronDown"
                    >
                      Load More Vendors
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section - aligned with homepage style */}
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-heading font-bold text-3xl text-text-primary">
            Are You a Wedding Vendor?
          </h2>
          <p className="text-lg text-text-secondary font-body">
            Join our marketplace and connect with couples planning their dream South Asian weddings.
          </p>
          <Button
            variant="primary"
            size="lg"
            iconName="UserPlus"
            onClick={() => navigate('/vendor-registration')}
          >
            Join as Vendor
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AllVendors;